import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MemberAvatar } from '@/components/clubs'
import { BookThumb } from '@/components/insights'
import type { ChallengeEntry, ClubMember } from '@/types'
import { formatDay } from './ChallengeMeta'
import { EntryComments } from './EntryComments'

interface EntryRowProps {
  entry: ChallengeEntry
  members: Map<string, ClubMember>
  commentCount: number
  canModerate: boolean
  isMine: boolean
}

/** One member's pick for a category, with its comment thread */
export function EntryRow({ entry, members, commentCount, canModerate, isMine }: EntryRowProps) {
  const { t, i18n } = useTranslation()
  const [showComments, setShowComments] = useState(false)
  const member = members.get(entry.user_id)

  return (
    <li className="space-y-2 rounded-lg border border-border p-3">
      <div className="flex items-center gap-3">
        <BookThumb src={entry.book_cover_url} title={entry.book_title} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{entry.book_title}</p>
          {entry.book_author && (
            <p className="truncate text-xs text-muted-foreground">{entry.book_author}</p>
          )}
          <div className="mt-1 flex items-center gap-1.5">
            <MemberAvatar
              userId={entry.user_id}
              name={member?.display_name ?? '?'}
              avatarUrl={member?.avatar_url}
              size="xs"
            />
            <span className="truncate text-xs text-muted-foreground">
              {isMine
                ? t('clubs.members.you')
                : (member?.display_name ?? t('challenges.formerMember'))}{' '}
              ·{' '}
              {entry.completed_on
                ? `✅ ${t('challenges.entry.readOn', { date: formatDay(entry.completed_on, i18n.language) })}`
                : `📖 ${t('challenges.entry.picked')}`}
            </span>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShowComments((v) => !v)}
        className="text-xs font-medium text-primary hover:underline"
      >
        💬{' '}
        {showComments
          ? t('challenges.comments.hide')
          : commentCount === 0
            ? t('challenges.comments.add')
            : t('challenges.comments.count', { count: commentCount })}
      </button>
      {showComments && <EntryComments entry={entry} members={members} canModerate={canModerate} />}
    </li>
  )
}
